<template>
  <tbody>
    <tr
      class="bg-orange-200/30 text-sm child:py-3 first-child:pl-3 last-child:pr-3 child:border-b child:border-gray-200 align-middle"
      :data-scope-gap-category-id="category.id"
      :class="{ 'cursor-pointer': !isEditing }"
      @click="toggleExpanded"
    >
      <td class="text-left font-semibold text-gray-700">
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="btn-icon btn-icon-default"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 transition-transform"
              :class="{ 'rotate-90': expanded }">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
          </button>

          <div
            v-if="showDragHandle"
            class="scope-gap-category-drag-handle cursor-grab active:cursor-grabbing p-1 rounded-md hover:bg-gray-200 transition-colors"
            title="Drag to reorder category"
            @click.stop
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 8h16M4 16h16"
    />
</svg>
          </div>

          <div class="min-w-0 flex-1 flex items-center gap-2">
            <template v-if="canEdit">
              <button
                v-if="isEditing"
                type="button"
                class="btn-icon btn-icon-red shrink-0"
                @click.stop="cancel"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
              </button>

              <button
                v-else
                type="button"
                class="btn-icon btn-icon-blue shrink-0"
                title="Edit category"
                @click.stop="startEditing"
              >
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
              </button>

              <button
                v-if="!isEditing"
                type="button"
                class="btn-icon btn-icon-red shrink-0"
                title="Delete category"
                @click.stop="deleteCategory"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
              </button>

              <button
                v-if="isEditing"
                type="button"
                class="btn-icon-green btn-icon shrink-0"
                @click.stop="save"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
              </button>
            </template>

            <AppInput
              v-if="canEdit && isEditing"
              ref="descriptionInput"
              v-model="description"
              name="category_description"
              type="text"
              class="w-full max-w-md form-input h-8"
              @click.stop
            />

            <span
              v-else
              class="min-w-0"
            >
              {{ category.description }}
            </span>
          </div>
        </div>
      </td>

      <td class="text-right font-semibold text-gray-900 content-center pr-3">
        {{ australianCurrency(totals.finalAmount) }}
      </td>

      <td />

      <td />

      <td />

      <td />

      <td v-if="showPercentages" />

      <td v-if="showPercentages" />

      <td v-if="showPercentages" />

      <td v-if="showForecasts" />

      <td v-if="ratesColumn" />

      <td />

      <td />

      <td />

      <td class="text-right font-semibold text-gray-900 content-center pr-3">
        <p>
          {{ australianCurrency(totals.currentForecast) }}
        </p>

        <p
          class="text-xs"
          :class="totalsVarianceClass"
        >
          {{ australianCurrency(totals.variance) }} m-1
        </p>
      </td>
    </tr>

    <Sortable
      v-if="expanded && monthlyForecasts.length"
      :list="monthlyForecasts"
      item-key="id"
      tag="template"
      class="bg-white divide-y divide-gray-200 relative contents"
      :class="{ 'opacity-50 pointer-events-none': updateItemSortAction.is(States.LOADING) }"
      :disabled="!canEdit"
      :options="{
        handle: '.scope-gap-item-drag-handle'
      }"
      @end="changeSortOrder"
    >
      <template #item="{ element }">
        <ScopeGapItemRow
          :scope-gap-item="element.scope_gap_item"
          :current-forecast="element"
          :previous-forecast="element.scope_gap_item.monthly_forecasts?.length > 0 ? element.scope_gap_item.monthly_forecasts[0] : undefined"
          :can-edit="canEdit"
          :show-drag-handle="canEdit"
          :show-percentages="showPercentages"
          :show-forecasts="showForecasts"
          :rates-column="ratesColumn"
          @refresh="emit('refreshData')"
        />
      </template>
    </Sortable>
  </tbody>
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency } from '@/helpers';
import MonthlyScopeGapForecast from '@/models/MonthlyScopeGapForecast';
import ScopeGap from '@/models/ScopeGap';
import ScopeGapCategory from '@/models/ScopeGapCategory';
import { useMonthlyScopeGapForecastsStore } from '@/store/monthlyScopeGapForecasts';
import { useScopeGapCategoriesStore } from '@/store/scopeGapCategories';
import { useScopeGapsStore } from '@/store/scopeGaps';
import { Sortable } from 'sortablejs-vue3';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import ScopeGapItemRow from './ScopeGapItemRow.vue';

const props = withDefaults(
  defineProps<{
    category: ScopeGapCategory;
    previousScopeGap?: ScopeGap | undefined;
    scopeGap: ScopeGap;
    canEdit: boolean;
    showDragHandle?: boolean;
    showPercentages?: boolean;
    showForecasts?: boolean;
    ratesColumn?: boolean;
  }>(),
  {
    showDragHandle: false,
    showPercentages: false,
    showForecasts: false,
    ratesColumn: false,
  },
);

const emit = defineEmits<{
  (e: 'refreshData'): void;
}>();

const scopeGapCategoriesStore = useScopeGapCategoriesStore();
const scopeGapsStore = useScopeGapsStore();
const fetchScopeGapShowAction = useStoreApiAction(scopeGapsStore.show);
const updateCategoryAction = useStoreApiAction(scopeGapCategoriesStore.update);
const destroyCategoryAction = useStoreApiAction(scopeGapCategoriesStore.destroy);
const description = ref(props.category.description);
const isEditing = ref(false);
const descriptionInput = useTemplateRef('descriptionInput');
const expanded = ref(false);

function toggleExpanded() {
  if(isEditing.value) return;

  expanded.value = !expanded.value;
}

function startEditing() {
  if(!props.canEdit) return;

  description.value = props.category.description;
  isEditing.value = true;

  nextTick(() => descriptionInput.value?.focusInput());
}

function cancel() {
  description.value = props.category.description;
  isEditing.value = false;
}

async function save() {
  if(description.value === props.category.description) {
    isEditing.value = false;

    return;
  }

  await updateCategoryAction.request(props.category.id, {
    description: description.value,
  });

  isEditing.value = false;
}

function deleteCategory() {
  if(!props.canEdit) return;

  Swal.fire({
    icon: 'warning',
    title: 'Delete category?',
    text: 'Are you sure you want to delete this category? Items in this category will become uncategorized.',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      destroyCategoryAction.request(props.category.id).then(() => {
        fetchScopeGapShowAction.request(props.scopeGap.id);
      });
    }
  });
}

const monthlyScopeGapForecastsStore = useMonthlyScopeGapForecastsStore();
const updateItemSortAction = useStoreApiAction(monthlyScopeGapForecastsStore.sort);

const monthlyForecasts = computed(() => {
  return monthlyScopeGapForecastsStore.models
    .query()
    .where('scope_gap_id', props.scopeGap.id)
    .where('scope_gap_category_id', props.category.id)
    .whereHas('scope_gap_item', (query) => {
      query.where('scope_gap_category_id', props.category.id);
    })
    .with('scope_gap_item', (query) => {
      if(props.previousScopeGap) {
        query.with('monthly_forecasts', (query) => {
          query.where('scope_gap_id', props.previousScopeGap.id);
        });
      }
    })
    .orderBy('sort_order')
    .get();
});

const totals = computed(() => {
  const result = {
    finalAmount: 0,
    currentForecast: 0,
    previousForecast: 0,
    variance: 0,
  };

  monthlyForecasts.value.forEach((monthlyForecast) => {
    const previousAmount = monthlyForecast.scope_gap_item?.monthly_forecasts?.[0]?.forecast_amount ?? 0;

    result.finalAmount += monthlyForecast.scope_gap_item?.final_amount ?? 0;
    result.currentForecast += monthlyForecast.forecast_amount ?? 0;
    result.previousForecast += previousAmount;
    result.variance += (monthlyForecast.forecast_amount ?? 0) - previousAmount;
  });

  return result;
});

const totalsVarianceClass = computed(() => {
  if(totals.value.variance === 0) return 'text-gray-900';

  return totals.value.variance > 0 ? 'text-red-600' : 'text-green-600';
});

const changeSortOrderForMonthlyForecast = (monthlyForecast: MonthlyScopeGapForecast, index: number) => {
  updateItemSortAction.request(monthlyForecast.id, {
    sort_order: index,
  });
};

const changeSortOrder = (e) => {
  const monthlyForecast = monthlyForecasts.value.at(e.oldIndex);

  changeSortOrderForMonthlyForecast(monthlyForecast, e.newIndex + 1);
};
</script>
